/**
 * Deeplink to Universal Link Mapper
 * Converts meditopia:// deeplinks to http://meditopia.com universal links
 * for iOS WebView compatibility
 */

// Deeplink aliases for common variations
const DEEPLINK_ALIASES: { [key: string]: string } = {
  'meditopia://breathing': 'meditopia://startbreathexercise',
  'meditopia://breath': 'meditopia://startbreathexercise',
  'meditopia://meditation': 'meditopia://quickstart',
  'meditopia://program': 'meditopia://programs',
  'meditopia://mood': 'meditopia://home',
  'meditopia://journal': 'meditopia://notes',
  'meditopia://wellnesspass': 'meditopia://home',
  'meditopia://yoga': 'meditopia://home',
};

// Static deeplink mappings
const DEEPLINK_MAPPINGS: { [key: string]: string } = {
  'meditopia://home': 'http://meditopia.com/en/home',
  'meditopia://programs': 'http://meditopia.com/en/programs',
  'meditopia://quickstart': 'http://meditopia.com/en/quickstart',
  'meditopia://sleep': 'http://meditopia.com/en/sleep',
  'meditopia://talks': 'http://meditopia.com/en/talks',
  'meditopia://nature': 'http://meditopia.com/en/nature',
  'meditopia://favorites': 'http://meditopia.com/en/favorites',
  'meditopia://notes': 'http://meditopia.com/en/notes',
  'meditopia://share': 'http://meditopia.com/en/share',
  'meditopia://daily': 'http://meditopia.com/en/daily',
  'meditopia://challenges': 'http://meditopia.com/en/challenges',
  'meditopia://challengedetail': 'http://meditopia.com/en/challengedetail',
  'meditopia://myjourney': 'http://meditopia.com/en/myjourney',
  'meditopia://startbreathexercise': 'http://meditopia.com/en/startbreathexercise',
  'meditopia://music': 'http://meditopia.com/en/music',
  'meditopia://settings': 'http://meditopia.com/en/settings',
  'meditopia://language': 'http://meditopia.com/en/language',
};

// Wildcard pattern mappings (for paths with IDs)
const WILDCARD_MAPPINGS: { base: string; webBase: string }[] = [
  { base: 'meditopia://redeem/', webBase: 'http://meditopia.com/en/redeem/' },
  { base: 'meditopia://play/', webBase: 'http://meditopia.com/en/play/' },
  { base: 'meditopia://playstory/', webBase: 'http://meditopia.com/en/playstory/' },
  { base: 'meditopia://playmusic/', webBase: 'http://meditopia.com/en/playmusic/' },
  { base: 'meditopia://playnature/', webBase: 'http://meditopia.com/en/playnature/' },
  { base: 'meditopia://playtalk/', webBase: 'http://meditopia.com/en/playtalk/' },
  { base: 'meditopia://programs/', webBase: 'http://meditopia.com/en/programs/' },
  { base: 'meditopia://collection/', webBase: 'http://meditopia.com/en/collection/' },
  { base: 'meditopia://payment/', webBase: 'http://meditopia.com/en/payment/' },
  { base: 'meditopia://inspiration/', webBase: 'http://meditopia.com/en/inspiration/' },
  { base: 'meditopia://profile/', webBase: 'http://meditopia.com/en/profile/' },
  { base: 'meditopia://mindfulstats/', webBase: 'http://meditopia.com/en/mindfulstats/' },
  { base: 'meditopia://playlist/', webBase: 'http://meditopia.com/en/playlist/' },
  { base: 'meditopia://playlistchat/', webBase: 'http://meditopia.com/en/playlistchat/' },
  { base: 'meditopia://talkdetail/', webBase: 'http://meditopia.com/en/talkdetail/' },
  { base: 'meditopia://musicdetail/', webBase: 'http://meditopia.com/en/musicdetail/' },
  { base: 'meditopia://storydetail/', webBase: 'http://meditopia.com/en/storydetail/' },
  { base: 'meditopia://wellnesspassqrscan/', webBase: 'http://meditopia.com/en/wellnesspassqrscan/' },
  { base: 'meditopia://yogadetail/', webBase: 'http://meditopia.com/en/yogadetail/' },
  { base: 'meditopia://playyoga/', webBase: 'http://meditopia.com/en/playyoga/' },
  { base: 'meditopia://coursedetail/', webBase: 'http://meditopia.com/en/coursedetail/' },
  { base: 'meditopia://playcourse/', webBase: 'http://meditopia.com/en/playcourse/' },
  { base: 'meditopia://exercisedetail/', webBase: 'http://meditopia.com/en/exercisedetail/' },
  { base: 'meditopia://playexercise/', webBase: 'http://meditopia.com/en/playexercise/' },
];

/**
 * Convert a deeplink to its corresponding universal web URL
 * @param link - The link (can be deeplink or already a web URL)
 * @returns The universal web URL
 */
export function toUniversalLink(link: string | undefined): string | undefined {
  if (!link) return undefined;

  // Already a web URL — convert https to http for iOS WebView compatibility
  if (link.startsWith('https://')) {
    return link.replace('https://', 'http://');
  }
  if (link.startsWith('http://')) {
    return link;
  }

  // Not a meditopia deeplink, return as-is
  if (!link.startsWith('meditopia://')) {
    return link;
  }

  // Check aliases first
  const normalizedLink = DEEPLINK_ALIASES[link] || link;

  // Try exact match
  if (DEEPLINK_MAPPINGS[normalizedLink]) {
    return DEEPLINK_MAPPINGS[normalizedLink];
  }

  // Try wildcard pattern matching
  for (const mapping of WILDCARD_MAPPINGS) {
    if (normalizedLink.startsWith(mapping.base)) {
      const suffix = normalizedLink.substring(mapping.base.length);
      return mapping.webBase + suffix;
    }
  }

  // Fallback: convert deeplink path to web URL
  const path = normalizedLink.replace('meditopia://', '');
  return `http://meditopia.com/en/${path}`;
}

/**
 * Content-playback URL paths that should be opened in a full-screen WebView.
 * Breathing exercises and navigation destinations (quickstart, home, etc.)
 * must NOT receive this flag.
 */
const PLAYER_PATHS = [
  '/play/',
  '/playstory/',
  '/playmusic/',
  '/playnature/',
  '/playtalk/',
  '/playyoga/',
  '/playcourse/',
  '/playexercise/',
];

/**
 * Append `presentOverWebView=true` to player URLs so iOS WebView opens them
 * as full-screen content players. No-op for non-player URLs and falsy input.
 */
export function withPresentOverWebView(url: string | undefined): string | undefined {
  if (!url) return url;
  if (!PLAYER_PATHS.some((p) => url.includes(p))) return url;
  if (/[?&]presentOverWebView=true\b/.test(url)) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}presentOverWebView=true`;
}
